// src/Hooks/useKmlManage.js

import { useEffect } from 'react';
import { useKmlState } from './useKmlState';
import { useKmlParser } from './useKmlParser';
import { useKmlFirebase } from './useKmlFirebase';

export const useKmlManage = ({ setIsDirty }) => {
  const stateStore = useKmlState();

  // Inject handleClearData directly into uiActions so Firebase hook can access it on success
  const handleClearData = (resetView = true) => {
    stateStore.setters.setParsedData([]);
    stateStore.setters.setSelectedImportIds([]);
    stateStore.setters.setCustomRows([]);
    stateStore.setters.setSelectedCustomIds([]);
    stateStore.setters.setRowMappings({});
    stateStore.setters.setRowSubMappings({});
    stateStore.setters.setRowStaticValues({});
    stateStore.setters.setOriginalFilename('');
    stateStore.setters.setCurrentFilename('');
    stateStore.setters.setGlobalCategory('');
    if (stateStore.setters.setPendingReinstateBatchIds) {
      stateStore.setters.setPendingReinstateBatchIds([]);
    }
    if (setIsDirty) setIsDirty(false);
    if (resetView) {
      stateStore.setters.setViewMode('DATA_VIEW');
    }
    stateStore.setters.setIsMinimized(false);
  };
  stateStore.uiActions.handleClearData = handleClearData;

  const parser = useKmlParser(stateStore);
  const firebase = useKmlFirebase(stateStore);

  const { state, setters, flags, uiActions } = stateStore;

  // Set default public date string using the standard days setting
  const d = new Date();
  d.setDate(d.getDate() + Number(state.makePublicDefaultDays));
  const defaultPublicFromDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const effectiveHasData =
    flags.hasData || (state.parsedData && state.parsedData.length > 0);

  // Robustly push the dirty status up to the parent panel
  useEffect(() => {
    if (setIsDirty) setIsDirty(effectiveHasData);
  }, [effectiveHasData, setIsDirty]);

  const handleCloseClick = () => {
    const hasUnimported = state.parsedData.some((item) => !item.isImported);
    if (state.parsedData.length > 0 && hasUnimported) {
      setters.setShowCloseConfirm(true);
    } else {
      handleClearData(true);
    }
  };

  const confirmClearData = () => {
    setters.setShowCloseConfirm(false);
    handleClearData(true);
  };

  const handleDeleteClick = () => {
    if (state.viewMode === 'HISTORY_VIEW') {
      setters.setShowDeleteConfirm(true);
    } else if (state.viewMode === 'SETTINGS_VIEW') {
      setters.setCustomRows((prev) =>
        prev.filter((row) => !state.selectedCustomIds.includes(row.id)),
      );
      setters.setRowMappings((prev) => {
        const next = { ...prev };
        state.selectedCustomIds.forEach((id) => delete next[id]);
        return next;
      });
      setters.setRowSubMappings((prev) => {
        const next = { ...prev };
        state.selectedCustomIds.forEach((id) => delete next[id]);
        return next;
      });
      setters.setRowStaticValues((prev) => {
        const next = { ...prev };
        state.selectedCustomIds.forEach((id) => delete next[id]);
        return next;
      });
      setters.setSelectedCustomIds([]);
    } else if (state.viewMode === 'DATA_VIEW') {
      if (state.selectedImportIds.length > 0) {
        setters.setConfirmDialog({
          open: true,
          message:
            'Do you want to permanently delete the selected markers from the queue?',
          confirmText: 'OK',
          cancelText: 'Cancel',
          severity: 'warning',
          onConfirm: () => {
            const remaining = state.parsedData.filter(
              (row) => !state.selectedImportIds.includes(row.id),
            );

            if (remaining.length === 0) {
              handleClearData(false);
            } else {
              setters.setParsedData(remaining);
              setters.setSelectedImportIds([]);
            }
            setters.setConfirmDialog((prev) => ({ ...prev, open: false }));
          },
        });
      }
    }
  };

  const handleReinstateClick = () => {
    if (state.selectedHistoryIds.length === 0) {
      setters.setAlertState({
        open: true,
        message: 'Please select at least one batch to reinstate.',
        severity: 'warning',
      });
      return;
    }
    firebase.executeReinstateBatch();
  };

  const handleMoveClick = () => {
    setters.setShowMoveConfirm(true);
  };

  const handleSaveRequest = () => {
    let rowsToProcess = [];
    if (state.selectedImportIds.length > 0) {
      rowsToProcess = state.parsedData.filter(
        (row) => state.selectedImportIds.includes(row.id) && !row.isImported,
      );
    } else {
      rowsToProcess = state.parsedData.filter((row) => !row.isImported);
    }

    if (rowsToProcess.length === 0) {
      setters.setAlertState({
        open: true,
        message: 'No data selected to import.',
        severity: 'warning',
      });
      return;
    }
    setters.setShowBatchFilenameDialog(true);
  };

  const confirmImportBatch = (filename) => {
    setters.setCurrentFilename(filename);
    setters.setShowBatchFilenameDialog(false);
    firebase.executeSave(filename);
  };

  return {
    state: {
      fileInputRef: stateStore.refs.fileInputRef,
      viewMode: state.viewMode,
      parsedData: state.parsedData,
      originalFilename: state.originalFilename,
      currentFilename: state.currentFilename,
      selectedImportIds: state.selectedImportIds,
      categories: state.categories,
      regions: state.regions,
      globalCategory: state.globalCategory,
      customRows: state.customRows,
      markerIcons: state.markerIcons,
      selectedCustomIds: state.selectedCustomIds,
      rowMappings: state.rowMappings,
      rowSubMappings: state.rowSubMappings,
      rowStaticValues: state.rowStaticValues,
      defaultPublicFromDate: defaultPublicFromDate,
      makeUppercase: state.makeUppercase,
      isProcessing: state.isProcessing,
      importHistory: state.importHistory,
      isLoadingHistory: state.isLoadingHistory,
      selectedHistoryIds: state.selectedHistoryIds,
    },
    flags: {
      hasData: flags.hasData,
      canSave: flags.canSave,
      canDeleteData: flags.canDeleteData,
      canUndoHistory: flags.canUndoHistory,
    },
    alerts: {
      alertState: state.alertState,
      showDeleteConfirm: state.showDeleteConfirm,
      showDeleteConfirm2: state.showDeleteConfirm2,
      showMoveConfirm: state.showMoveConfirm,
      showCloseConfirm: state.showCloseConfirm,
      restrictedAlert: state.restrictedAlert,
      showBatchFilenameDialog: state.showBatchFilenameDialog,
      confirmDialog: state.confirmDialog,
    },
    actions: {
      setSelectedImportIds: setters.setSelectedImportIds,
      setGlobalCategory: setters.setGlobalCategory,
      setSelectedHistoryIds: setters.setSelectedHistoryIds,
      setAlertState: setters.setAlertState,
      setShowDeleteConfirm: setters.setShowDeleteConfirm,
      setShowDeleteConfirm2: setters.setShowDeleteConfirm2,
      setShowMoveConfirm: setters.setShowMoveConfirm,
      setShowCloseConfirm: setters.setShowCloseConfirm,
      setShowBatchFilenameDialog: setters.setShowBatchFilenameDialog,
      setConfirmDialog: setters.setConfirmDialog,
      handleAddCustomRow: uiActions.handleAddCustomRow,
      handleCustomRowNameChange: uiActions.handleCustomRowNameChange,
      setSelectedCustomIds: setters.setSelectedCustomIds,
      setRowMappings: setters.setRowMappings,
      setRowSubMappings: setters.setRowSubMappings,
      setRowStaticValues: setters.setRowStaticValues,
      handleNavFirst: uiActions.handleNavFirst,
      handleNavBack: uiActions.handleNavBack,
      handleNavForward: uiActions.handleNavForward,
      handleNavLast: uiActions.handleNavLast,
      handleImportClick: parser.handleImportClick,
      handleFileChange: parser.handleFileChange,
      handleCloseClick,
      confirmClearData,
      handleSave: handleSaveRequest,
      confirmImportBatch,
      executeDeleteBatches: firebase.executeDeleteBatches,
      handleMoveClick,
      executeMoveBatches: firebase.executeMoveBatches,
      handleDeleteClick,
      handleAcknowledgeRestricted: uiActions.handleAcknowledgeRestricted,
      handleReinstateClick,
      executeReinstateBatch: firebase.executeReinstateBatch,
    },
  };
};
